import React, { useState, useEffect, useRef } from "react";

function Distance() {
  const [distance, setDistance] = useState(0);
  const [previousPosition, setPreviousPosition] = useState(null);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [isTracking, setIsTracking] = useState(true);
  const [error, setError] = useState(null);
  const watchId = useRef(null);

  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setError("Geolocation is not supported by this browser.");
      return;
    }

    if (isTracking) {
      watchId.current = navigator.geolocation.watchPosition(
        (position) => {
          const { latitude, longitude, accuracy } = position.coords;

          if (accuracy > 10) {
            console.log("Ignoring low accuracy reading.");
            return;
          }

          const newPosition = { latitude, longitude };

          setCurrentPosition(newPosition);

          if (previousPosition) {
            const newDistance = calculateDistance(
              previousPosition.latitude,
              previousPosition.longitude,
              latitude,
              longitude
            );

            if (newDistance > 0.5) { // Allow very small movements
              setDistance((prevDistance) => prevDistance + newDistance);
              setPreviousPosition(newPosition);
            }
          } else {
            setPreviousPosition(newPosition);
          }
        },
        (err) => {
          switch (err.code) {
            case 1:
              setError("Permission denied. Please enable location access.");
              break;
            case 2:
              setError("Position unavailable. Try moving to an open area.");
              break;
            case 3:
              setError("Request timed out. Try again.");
              break;
            default:
              setError("An unknown error occurred.");
              break;
          }
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 10000,
        }
      );
    } else if (watchId.current) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }

    return () => {
      if (watchId.current) {
        navigator.geolocation.clearWatch(watchId.current);
      }
    };
  }, [isTracking, previousPosition]);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth's radius in meters
    const toRad = (value) => (value * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  const handleReset = () => {
    setDistance(0);
    setPreviousPosition(null);
    setCurrentPosition(null);
    setError(null);
  };

  const handlePauseResume = () => {
    setIsTracking(!isTracking);
  };

  return (
    <div style={{ textAlign: "center", marginTop: "50px" }}>
      <h1>Distance Tracker</h1>
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      <p>Distance Traveled: {distance.toFixed(2)} meters</p>
      {currentPosition && (
        <p>
          Current Position: Latitude {currentPosition.latitude.toFixed(14)}, Longitude{" "}
          {currentPosition.longitude.toFixed(14)}
        </p>
      )}
      <div>
        <button onClick={handlePauseResume} style={{ margin: "5px" }}>
          {isTracking ? "Pause" : "Resume"}
        </button>
        <button onClick={handleReset} style={{ margin: "5px" }}>
          Reset
        </button>
      </div>
    </div>
  );
}

export default Distance;
